import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { AdminEndpointKey } from "@/services/api/admin-registry";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ApiHealthStatus = "live" | "degraded" | "offline";

export interface ApiStatusEntry {
  status: ApiHealthStatus;
  lastCheckAt: number;
  lastHttpStatus: number;
  note?: string;
}

export interface ApiLogEntry {
  id: string;
  endpointKey: AdminEndpointKey;
  method: string;
  path: string;
  httpStatus: number;
  durationMs: number;
  timestamp: number;
  correlationId?: string;
  error?: string;
}

// ---------------------------------------------------------------------------
// Shared types (used across admin pages)
// ---------------------------------------------------------------------------

export interface AdminTenant {
  id: string;
  name: string;
  slug: string;
  settings: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const MAX_LOG_ENTRIES = 500;

interface AdminState {
  // API health per-endpoint
  apiStatus: Partial<Record<AdminEndpointKey, ApiStatusEntry>>;

  // API call log (LIFO, max 500)
  apiLogs: ApiLogEntry[];

  // Actions
  upsertApiStatus: (key: AdminEndpointKey, entry: ApiStatusEntry) => void;
  pushApiLog: (entry: ApiLogEntry) => void;
  clearApiLogs: () => void;
}

// ---------------------------------------------------------------------------
// Store (NO persist — session-only)
// ---------------------------------------------------------------------------

export const useAdminStore = create<AdminState>()(
  devtools(
    (set) => ({
      apiStatus: {},
      apiLogs: [],

      upsertApiStatus: (key, entry) => {
        set(
          (state) => ({
            apiStatus: { ...state.apiStatus, [key]: entry },
          }),
          false,
          "upsertApiStatus",
        );
      },

      pushApiLog: (entry) => {
        set(
          (state) => ({
            apiLogs: [entry, ...state.apiLogs].slice(0, MAX_LOG_ENTRIES),
          }),
          false,
          "pushApiLog",
        );
      },

      clearApiLogs: () => {
        set({ apiLogs: [] }, false, "clearApiLogs");
      },
    }),
    { name: "AdminStore", enabled: import.meta.env.DEV },
  ),
);

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectApiStatus = (s: AdminState) => s.apiStatus;
export const selectApiLogs = (s: AdminState) => s.apiLogs;
